<script setup lang="ts">
import { computed } from 'vue';
import { RouterView, useRouter, useRoute } from 'vue-router';
import { getMsal } from '@/auth/msal';
import { useAuthStore } from '@/stores/auth';
import { useFilesStore } from '@/stores/files';
import { useIdleTimeout } from '@/lib/useIdleTimeout';
import { routeForFilePath } from '@/lib/fileRoute';
import {
  beginManagedUnload,
  readRecoverableDrafts,
  recoveryDrawerVisible,
  saveRecoverableDrafts,
  type RecoverableDraft,
} from '@/lib/draftRecovery';
import { newSecretRequest, clearNewSecretRequest } from '@/lib/secretCreatePrompt';
import { newServiceRequest, clearNewServiceRequest } from '@/lib/serviceCreatePrompt';
import { secretEditRequest, clearEditSecretRequest } from '@/lib/secretEditPrompt';
import {
  secretFromValueRequest,
  resolveSecretFromValue,
  clearSecretFromValueRequest,
} from '@/lib/secretFromValuePrompt';
import { navDrawerVisible } from '@/lib/navHistory';
import { useEditorContextStore } from '@/stores/editorContext';
import IdleLogoutDialog from '@/components/IdleLogoutDialog.vue';
import DraftRecoveryDrawer from '@/components/DraftRecoveryDrawer.vue';
import NavHistoryDrawer from '@/components/NavHistoryDrawer.vue';
import NewSecretDialog from '@/components/NewSecretDialog.vue';
import NewServiceDialog from '@/components/NewServiceDialog.vue';
import SecretEditor from '@/components/SecretEditor.vue';

// Inactivity logout lives at the app root (not AppShell) so it covers EVERY route,
// including the public landing page and the unauthorized page. It only actually runs
// for a signed-in session — a not-logged-in visitor on `/` has nothing to log out of.
//
// Auth state is fixed for the lifetime of a page load: MSAL is initialised before the
// app mounts (main.ts), and every auth transition (loginRedirect / logoutRedirect) is
// a full-page navigation that re-runs this setup. So a one-time check here is correct
// and doesn't need to be reactive.
const auth = useAuthStore();
const files = useFilesStore();
const router = useRouter();
const route = useRoute();
const msal = getMsal();

// Recent-views drawer visibility. navDrawerVisible is the persisted source of truth
// (localStorage, restored on reload / re-login). Gate actual display to authenticated
// app routes so a restored-open state never pushes/overlays the public landing or
// unauthorized pages (which have no toolbar to toggle it back, and no AppShell). Writes
// (toolbar toggle, drawer close) flow straight back to the persisted ref.
const navDrawerOpen = computed<boolean>({
  get: () => navDrawerVisible.value && !!route.meta?.requiresAuth,
  set: (v) => { navDrawerVisible.value = v; },
});
// The persistent IDE-style left-edge handle shows on authenticated app routes
// whether the panel is open or not (it IS the open/close affordance). Hidden on
// mobile via CSS — Recent views is reached from the mobile nav footer there.
const showNavHandle = computed(() => !!route.meta?.requiresAuth);
const signedIn = (msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null) !== null;

// Build-time configurable via Vite env, same mechanism as the AAD settings (baked per
// build). Zero-config falls back to 5 min idle / 1 min warning.
function envSeconds(raw: unknown, fallback: number): number {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}
const idleTimeoutSeconds = envSeconds(import.meta.env.VITE_IDLE_TIMEOUT_SECONDS, 300);
const idleWarningSeconds = envSeconds(import.meta.env.VITE_IDLE_WARNING_SECONDS, 60);

const { warningActive, secondsRemaining, reset: resetIdle } = useIdleTimeout({
  timeoutSeconds: idleTimeoutSeconds,
  warningSeconds: idleWarningSeconds,
  enabled: signedIn,
  // Snapshot dirty editors for crash-recovery BEFORE sign-out navigates away. This is
  // synchronous and runs only on the idle path (not manual sign-out — that's a
  // deliberate user action). signOut then clears MSAL + hard-navigates.
  onTimeout: () => {
    const drafts: RecoverableDraft[] = files.dirtyPaths.map((path) => {
      const f = files.open[path]!;
      return {
        path,
        draft: f.draft,
        baseline: f.server.contents,
        sha256: f.server.sha256,
        capturedAt: Date.now(),
      };
    });
    saveRecoverableDrafts(drafts);
    // Suppress the unsaved-changes beforeunload prompt for this navigation only —
    // drafts are safe, and an unattended logout mustn't hang on a native dialog.
    beginManagedUnload();
    void auth.signOut();
  },
});

// Auto-open the recovery drawer once on the first sign-in after a timeout that captured
// drafts. Gated on signedIn so it never shows pre-login; sessionStorage survived the
// logout->login round-trip in this tab. After this, recoveryDrawerVisible is driven by
// the shared ref — the AppShell affordance can reopen it in-session without a reload.
if (signedIn && readRecoverableDrafts().length > 0) {
  recoveryDrawerVisible.value = true;
}

// Park the recovered draft, navigate to its editor, then ensure the tab is open.
// stageRecovered parks a NON-consuming pending draft: ServiceEditorView does
// closeAll()+openFile() on mount and ConfigEditorView doesn't auto-open any slot file —
// the parked draft is re-applied on every openFile and only cleared once the operator
// edits/saves/discards/closes, so it survives both shapes regardless of interleaving.
// The explicit openFile after navigation surfaces the tab for ConfigEditorView (which
// otherwise only opens files via FileTree clicks). routeForFilePath returns null for an
// unrecognised shape: the draft stays parked and is restored whenever the file is next
// opened (FileTree / manual nav) — we just don't auto-navigate.
async function onRecover(d: RecoverableDraft) {
  files.stageRecovered(d.path, d.draft);
  const target = routeForFilePath(d.path);
  if (target) {
    await router.push(target);
    await files.openFile(d.path);
  }
}

// Global "Create secret" host. The Monaco quick-fix on a not-found $__secret squiggle
// calls requestNewSecret(scope:name); this dialog opens prefilled so the operator sets
// slot values exactly as a normal create. On success, refresh every cached editor-
// context slot so monaco-completions' marker watcher clears the squiggle.
const editorContext = useEditorContextStore();
async function refreshCachedSlots() {
  await Promise.all(
    Object.keys(editorContext.secretsBySlot).map((slot) => editorContext.refreshSlot(slot)),
  );
}
async function onSecretCreated() {
  clearNewSecretRequest();
  await refreshCachedSlots();
}

// Global "Edit secret values" host. The Monaco code action on a RESOLVABLE $__secret
// reference calls requestEditSecret(scope, name); SecretEditor opens for that reference.
// On close, refresh cached slots — the operator may have added the secret to a slot or
// tombstoned one, which changes what monaco-completions considers resolvable.
async function onSecretEditorClosed() {
  clearEditSecretRequest();
  await refreshCachedSlots();
}

// "Create secret from this value" host. The dialog reports the created reference;
// resolveSecretFromValue runs the editor's pending replacement (literal → marker
// object) and clears the request. Refresh cached slots so the new secret resolves.
async function onSecretFromValueCreated(reference: string) {
  resolveSecretFromValue(reference);
  await refreshCachedSlots();
}
</script>

<template>
  <!-- Recent-views drawer is a "push" drawer: while it's open the routed content
       (the whole AppShell) is shifted right by the drawer's width so nothing is
       hidden behind it. The drawer itself teleports to body and is NOT inside this
       wrapper, so only the content moves. Width/maxWidth here MUST match
       NavHistoryDrawer's :style. -->
  <div class="app-shift" :class="{ 'nav-open': navDrawerOpen }">
    <RouterView />
  </div>

  <!-- App-root mounts so these show on any route. PrimeVue Dialog teleports to body and
       the PrimeVue plugin is app-level, so they work outside AppShell. -->
  <IdleLogoutDialog
    :visible="warningActive"
    :seconds="secondsRemaining"
    :warning-seconds="idleWarningSeconds"
    @stay="resetIdle"
  />

  <DraftRecoveryDrawer
    v-model:visible="recoveryDrawerVisible"
    @recover="onRecover"
  />

  <!-- Recent-views history. App-root host so open state + content survive AppShell's
       per-route remount. Toggled by the persistent left-edge handle below (and the
       mobile nav footer). -->
  <NavHistoryDrawer v-model:visible="navDrawerOpen" />

  <!-- Persistent IDE-style edge handle: always visible on authed routes, rides at the
       panel's right edge when open. Deliberate open/close only (ESC is disabled on the
       drawer); hidden on mobile (Recent views is in the mobile nav footer there). -->
  <button
    v-if="showNavHandle"
    type="button"
    class="nav-handle"
    :class="{ open: navDrawerOpen }"
    :aria-expanded="navDrawerOpen"
    :aria-label="navDrawerOpen ? 'Collapse recent views' : 'Open recent views'"
    title="Recent views"
    @click="navDrawerOpen = !navDrawerOpen"
  >
    <i :class="navDrawerOpen ? 'pi pi-chevron-left' : 'pi pi-chevron-right'" aria-hidden="true" />
  </button>

  <NewSecretDialog
    :visible="newSecretRequest !== null"
    :prefill-reference="newSecretRequest?.reference ?? ''"
    @update:visible="(v: boolean) => { if (!v) clearNewSecretRequest(); }"
    @created="onSecretCreated"
  />

  <!-- Editor "Create service" quick-fix host. Prefilled, no post-create navigation
       (the operator stays in the config file). The services-store reload inside the
       dialog trips monaco-completions' marker watcher, clearing the squiggle. -->
  <NewServiceDialog
    :visible="newServiceRequest !== null"
    :prefill-name="newServiceRequest?.name ?? ''"
    :navigate-on-create="false"
    @update:visible="(v: boolean) => { if (!v) clearNewServiceRequest(); }"
    @created="clearNewServiceRequest"
  />

  <!-- Editor "Edit secret values…" host (resolvable $__secret references). -->
  <SecretEditor :target="secretEditRequest" @close="onSecretEditorClosed" />

  <!-- Editor "Create secret from this value…" host. Prefills the edited file's slot
       with the extracted literal; on create, the editor rewrites the value to the
       marker object (left dirty for the operator to Save). -->
  <NewSecretDialog
    :visible="secretFromValueRequest !== null"
    :prefill-slot-value="secretFromValueRequest ?? undefined"
    @update:visible="(v: boolean) => { if (!v) clearSecretFromValueRequest(); }"
    @created="onSecretFromValueCreated"
  />
</template>

<style scoped>
/* Push-drawer offset for the Recent-views drawer. The wrapper is normal block flow, so
   padding-left shrinks its content box and the child AppShell (.shell, width follows
   the content box) reflows to the right — nothing ends up hidden behind the drawer. No
   horizontal scrollbar because a block element's box never exceeds the containing block.
   min()/90vw mirrors NavHistoryDrawer's width/maxWidth so the shift matches the drawer
   exactly, including on very narrow viewports. */
.app-shift {
  min-height: 100vh;
  transition: padding-left 0.22s ease;
}
.app-shift.nav-open {
  padding-left: min(22rem, 90vw);
}

/* Persistent left-edge handle for the Recent-views panel (IDE explorer style). Rides
   from the screen edge to the panel's right edge in lockstep with the push transition. */
.nav-handle {
  position: fixed;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  z-index: 1200;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 3.5rem;
  padding: 0;
  border: 1px solid var(--p-content-border-color, #e5e7eb);
  border-left: none;
  border-radius: 0 8px 8px 0;
  background: var(--p-content-background, #fff);
  color: var(--p-text-muted-color, #6b7280);
  cursor: pointer;
  box-shadow: 1px 0 4px rgba(0, 0, 0, 0.08);
  transition: left 0.22s ease, background 0.15s ease, color 0.15s ease;
}
.nav-handle:hover,
.nav-handle:focus-visible {
  color: var(--p-primary-color, #2563eb);
  background: var(--p-content-hover-background, #f3f4f6);
  outline: none;
}
.nav-handle.open { left: min(22rem, 90vw); }
.nav-handle i { font-size: 0.75rem; }

/* Mobile: no edge handle / push (a 90vw shove is unusable). Recent views is reached
   from the mobile nav drawer footer instead. */
@media (max-width: 960px) {
  .nav-handle { display: none; }
  .app-shift.nav-open { padding-left: 0; }
}
</style>
